export const DRAFT_FORMAT_VERSION = 1;
export const DEFAULT_DRAFT_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000;

export type DraftEnvelope<T> = {
  version: number;
  updatedAt: string;
  userId: string;
  screenType: string;
  entityId: string;
  data: T;
};

export type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function draftStorageKey(userId: string, screenType: string, entityId: string): string {
  return `draft:${userId}:${screenType}:${entityId}`;
}

export function saveDraftEnvelope<T>(
  storage: DraftStorage,
  envelope: DraftEnvelope<T>,
): void {
  storage.setItem(
    draftStorageKey(envelope.userId, envelope.screenType, envelope.entityId),
    JSON.stringify(envelope),
  );
}

export function readDraftEnvelope<T>(
  storage: DraftStorage,
  userId: string,
  screenType: string,
  entityId: string,
  now = Date.now(),
  expirationMs = DEFAULT_DRAFT_EXPIRATION_MS,
  version = DRAFT_FORMAT_VERSION,
): DraftEnvelope<T> | null {
  const key = draftStorageKey(userId, screenType, entityId);
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DraftEnvelope<T>;
    const updatedAt = new Date(parsed.updatedAt).getTime();
    const valid = parsed.version === version
      && parsed.userId === userId
      && parsed.screenType === screenType
      && parsed.entityId === entityId
      && Number.isFinite(updatedAt)
      && now - updatedAt <= expirationMs;
    if (!valid) {
      storage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function discardDraftEnvelope(
  storage: DraftStorage,
  userId: string,
  screenType: string,
  entityId: string,
): void {
  storage.removeItem(draftStorageKey(userId, screenType, entityId));
}
